import React, { memo, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { FaPlus, FaMinus } from "react-icons/fa";
import { MenuItem } from "../redux/slices/menuItemsSlice";
import { formatPrice } from "../utils/priceUtils";
import { getCartQuantity, getCartItemId } from "../utils/cartUtils";

interface PrimeMenuItemCardMobileProps {
  item: MenuItem;
  onItemClick?: (item: MenuItem) => void;
  onAddToCart?: (item: MenuItem) => void;
  showAddToCart?: boolean;
  cartItems?: any[];
  onUpdateQuantity?: (cartItemId: string, quantity: number) => void;
  onIncrementQuantity?: (item: MenuItem) => void;
  onDecrementQuantity?: (itemId: string) => void;
}

/**
 * ✅ Mobile-first, accessible, and optimized menu item card
 * - Prioritizes small screens first, scales up cleanly
 * - Optimized for smooth animations & accessibility
 * - Implements proper semantic HTML & alt text
 */
const PrimeMenuItemCardMobile: React.FC<PrimeMenuItemCardMobileProps> = ({
  item,
  onItemClick,
  onAddToCart,
  showAddToCart = true,
  cartItems = [],
  onUpdateQuantity,
  onIncrementQuantity,
  onDecrementQuantity,
}) => {
  if (!item.is_enabled || !item.is_item_verified) return null;

  const isOutOfStock = !item.inventoryStatus;

  // Get cart quantity for this item
  const cartQuantity = useMemo(() => 
    getCartQuantity(item.pk_id.toString(), cartItems),
    [item.pk_id, cartItems]
  );

  const cartItemId = useMemo(() => 
    getCartItemId(item.pk_id.toString()),
    [item.pk_id]
  );

  const handleImageError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      (e.target as HTMLImageElement).src = `https://via.placeholder.com/400x300/1a1a1a/d4af37?text=${encodeURIComponent(
        item.name
      )}`;
    },
    [item.name]
  );

  const handleCardClick = useCallback(() => {
    onItemClick?.(item);
  }, [onItemClick, item]);

  const handleAddToCart = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!isOutOfStock) onAddToCart?.(item);
    },
    [onAddToCart, item, isOutOfStock]
  );

  // Extract price from object or use as-is
  const displayPrice = typeof item.price === 'object' ? item.price.online : item.price;

  return (
    <motion.article
      className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden w-full cursor-pointer border border-slate-100 dark:border-slate-700"
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${item.name}`}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleCardClick()}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      data-testid={`menu-item-card-mobile-${item.id || item.pk_id}`}
    >
      {/* Conditional Layout: Horizontal with image, Full width without */}
      <div className={item.image ? "grid grid-cols-5 gap-0" : ""}>
        {/* 📦 Content Section */}
        <div className={`${item.image ? "col-span-3" : ""} p-4 flex flex-col justify-between`}>
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-1 line-clamp-1">
              {item.name}
            </h2>
            {item.description && (
              <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2 mb-3">
                {item.description}
              </p>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 mt-auto">
            <p className="text-lg font-bold text-slate-900 dark:text-white">
              {formatPrice(displayPrice)}
            </p>

            {showAddToCart && (
              cartQuantity > 0 ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onDecrementQuantity) {
                        onDecrementQuantity(item.pk_id.toString());
                      } else if (onUpdateQuantity) {
                        onUpdateQuantity(cartItemId, cartQuantity - 1);
                      }
                    }}
                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-all"
                    aria-label="Decrease quantity"
                  >
                    <FaMinus className="w-3 h-3 text-gray-700" />
                  </button>
                  <span className="font-bold text-lg text-gray-900 min-w-[2rem] text-center">{cartQuantity}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (cartQuantity > 0) {
                        if (onIncrementQuantity) {
                          onIncrementQuantity(item);
                        } else if (onUpdateQuantity) {
                          onUpdateQuantity(cartItemId, cartQuantity + 1);
                        }
                      } else {
                        handleAddToCart(e);
                      }
                    }}
                    className="w-8 h-8 rounded-full text-white flex items-center justify-center transition-all"
                    style={{ backgroundColor: '#EF4322' }}
                    aria-label="Increase quantity"
                  >
                    <FaPlus className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <motion.button
                  type="button"
                  disabled={isOutOfStock}
                  aria-disabled={isOutOfStock}
                  onClick={handleAddToCart}
                  aria-label={`Add ${item.name} to cart${isOutOfStock ? ' - Out of Stock' : ''}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-bold tracking-wide transition-all duration-300 shadow focus:outline-none focus:ring-4 ${
                    isOutOfStock
                      ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                      : "bg-orange-500 hover:bg-orange-600 focus:ring-orange-300 text-white"
                  }`}
                  whileHover={!isOutOfStock ? { scale: 1.02 } : {}}
                  whileTap={!isOutOfStock ? { scale: 0.96 } : {}}
                >
                  {isOutOfStock ? "Out of Stock" : "Add"}
                </motion.button>
              )
            )}
          </div>
        </div>

        {/* 🖼️ Image Section - Only render if image exists */}
        {item.image && (
          <div className="col-span-2 relative h-full min-h-[140px]">
            <div className="relative w-full h-full">
              <motion.img
                src={item.image}
                alt={`${item.name} menu item`}
                className="w-full h-full object-cover"
                onError={handleImageError}
                loading="lazy"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.4 }}
              />

            </div>
          </div>
        )}
      </div>
    </motion.article>
  );
};

const areEqual = (
  prev: PrimeMenuItemCardMobileProps,
  next: PrimeMenuItemCardMobileProps
) => {
  return (
    (prev.item.id || prev.item.pk_id) === (next.item.id || next.item.pk_id) &&
    prev.item.price === next.item.price &&
    prev.item.is_enabled === next.item.is_enabled &&
    prev.item.inventoryStatus === next.item.inventoryStatus &&
    prev.item.is_item_verified === next.item.is_item_verified &&
    prev.showAddToCart === next.showAddToCart &&
    prev.cartItems === next.cartItems
  );
};

export default memo(PrimeMenuItemCardMobile, areEqual);
